/**
 * TypeScript mirror of backend AreaAnalysis.
 * Accepts the dict from backend AreaAnalysis.to_dict() and exposes
 * AreaAnalysis.fromDict() to build an instance with the same logical shape as the Python class.
 */

/** Backend Money.to_dict() → { v: string, s: string } */
export interface MoneyLike {
    v: number | string
    s?: string
}

/** YearlyTrend with Python-style names (value, time_horizon_years, sample_size). */
export interface YearlyTrendLike {
    value: number
    time_horizon_years: number
    sample_size: number
}

/** Backend TimeSeries.to_dict() */
export interface TimeSeriesLike {
    v: number[]
    d: string[]
    tu?: string
}

/** Backend GeographicalCoordinates.to_dict() */
export interface GeographicalCoordinatesLike {
    lt: number | string
    ln: number | string
}

/** One recent sale item (backend DetailedAddress.to_dict()); shape left flexible. */
export type RecentSaleLike = Record<string, unknown>

/** Dict produced by backend AreaAnalysis.to_dict() (short keys). */
export type AreaAnalysisDict = Record<string, unknown>

function parseMoney(d: unknown): MoneyLike | null {
    if (d == null || typeof d !== "object" || !("v" in (d as object))) return null
    const o = d as Record<string, unknown>
    return { v: o.v as number | string, s: o.s as string }
}

function parseYearlyTrend(d: unknown): YearlyTrendLike | null {
    if (d == null || typeof d !== "object") return null
    const o = d as Record<string, unknown>
    // Require at least value (v); thy and s default so we don't drop trend when API omits them
    if (o.v == null && o.value == null) return null
    const v = o.v != null ? o.v : o.value
    const value = typeof v === "number" ? v : parseFloat(String(v))
    if (Number.isNaN(value)) return null
    const thy = o.thy != null ? o.thy : o.time_horizon_years
    const time_horizon_years = thy != null ? (typeof thy === "number" ? thy : parseInt(String(thy), 10)) : 3
    const s = o.s != null ? o.s : o.sample_size
    const sample_size = s != null ? (typeof s === "number" ? s : parseInt(String(s), 10)) : 0
    return {
        value,
        time_horizon_years: Number.isNaN(time_horizon_years) ? 3 : time_horizon_years,
        sample_size: Number.isNaN(sample_size) ? 0 : sample_size,
    }
}

function parseTimeSeries(d: unknown): TimeSeriesLike | null {
    if (d == null || typeof d !== "object" || !Array.isArray((d as Record<string, unknown>).v)) return null
    const o = d as Record<string, unknown>
    return {
        v: (o.v as number[]).map((x) => (typeof x === "number" ? x : parseFloat(String(x)))),
        d: (o.d as string[]) ?? [],
        tu: o.tu as string | undefined,
    }
}

function parseGeo(d: unknown): GeographicalCoordinatesLike | null {
    if (d == null || typeof d !== "object" || !("lt" in (d as object))) return null
    const o = d as Record<string, unknown>
    return {
        lt: o.lt as number | string,
        ln: o.ln as number | string,
    }
}

/**
 * TypeScript mirror of backend AreaAnalysis. Build from API dict with fromDict(d).
 */
export class AreaAnalysis {
    readonly one_year_inter_quartile_range_price: MoneyLike | null
    readonly one_year_median_price: MoneyLike | null
    readonly one_year_25th_percentile_price: MoneyLike | null
    readonly one_year_75th_percentile_price: MoneyLike | null
    readonly one_year_sales_count: number | null
    readonly year_on_year_median_price_change_percent: number | null
    readonly sales_per_month: TimeSeriesLike | null
    readonly median_sale_price_per_month: TimeSeriesLike | null
    readonly sales_per_year: TimeSeriesLike | null
    readonly median_sale_price_per_year: TimeSeriesLike | null
    readonly average_annual_trend: number | null
    readonly recent_sales: RecentSaleLike[] | null
    readonly centroid: GeographicalCoordinatesLike | null

    constructor(init: {
        one_year_inter_quartile_range_price: MoneyLike | null
        one_year_median_price: MoneyLike | null
        one_year_25th_percentile_price: MoneyLike | null
        one_year_75th_percentile_price: MoneyLike | null
        one_year_sales_count: number | null
        year_on_year_median_price_change_percent: number | null
        sales_per_month: TimeSeriesLike | null
        median_sale_price_per_month: TimeSeriesLike | null
        sales_per_year: TimeSeriesLike | null
        median_sale_price_per_year: TimeSeriesLike | null
        average_annual_trend: number | null
        recent_sales: RecentSaleLike[] | null
        centroid: GeographicalCoordinatesLike | null
    }) {
        this.one_year_inter_quartile_range_price = init.one_year_inter_quartile_range_price
        this.one_year_median_price = init.one_year_median_price
        this.one_year_25th_percentile_price = init.one_year_25th_percentile_price
        this.one_year_75th_percentile_price = init.one_year_75th_percentile_price
        this.one_year_sales_count = init.one_year_sales_count
        this.year_on_year_median_price_change_percent = init.year_on_year_median_price_change_percent
        this.sales_per_month = init.sales_per_month
        this.median_sale_price_per_month = init.median_sale_price_per_month
        this.sales_per_year = init.sales_per_year
        this.median_sale_price_per_year = init.median_sale_price_per_year
        this.average_annual_trend = init.average_annual_trend
        this.recent_sales = init.recent_sales
        this.centroid = init.centroid
    }

    /** Build an instance from the backend AreaAnalysis.to_dict() payload. */
    static fromDict(d: AreaAnalysisDict): AreaAnalysis {
        const get = (key: string): unknown => d[key]
        return new AreaAnalysis({
            one_year_inter_quartile_range_price: parseMoney(get("1yi")) ?? null,
            one_year_median_price: parseMoney(get("1ym")) ?? null,
            one_year_25th_percentile_price: parseMoney(get("1yq1")) ?? null,
            one_year_75th_percentile_price: parseMoney(get("1yq3")) ?? null,
            one_year_sales_count: get("1ys") != null ? Number(get("1ys")) : null,
            year_on_year_median_price_change_percent: get("yoym") != null ? Number(get("yoym")) : null,
            sales_per_month: parseTimeSeries(get("spm")) ?? null,
            median_sale_price_per_month: parseTimeSeries(get("mspm")) ?? null,
            sales_per_year: parseTimeSeries(get("spy")) ?? null,
            median_sale_price_per_year: parseTimeSeries(get("mspy")) ?? null,
            average_annual_trend: get("aat") != null ? Number(get("aat")) : null,
            recent_sales: Array.isArray(get("rs")) ? (get("rs") as RecentSaleLike[]) : null,
            centroid: parseGeo(get("gc")) ?? null,
        })
    }

    /** Convert back to the short-key dict shape expected by the backend / API. */
    toDict(): AreaAnalysisDict {
        const out: AreaAnalysisDict = {}
        if (this.one_year_inter_quartile_range_price != null) out["1yi"] = this.one_year_inter_quartile_range_price
        if (this.one_year_median_price != null) out["1ym"] = this.one_year_median_price
        if (this.one_year_25th_percentile_price != null) out["1yq1"] = this.one_year_25th_percentile_price
        if (this.one_year_75th_percentile_price != null) out["1yq3"] = this.one_year_75th_percentile_price
        if (this.one_year_sales_count != null) out["1ys"] = this.one_year_sales_count
        if (this.year_on_year_median_price_change_percent != null) out["yoym"] = this.year_on_year_median_price_change_percent
        if (this.sales_per_month != null) out["spm"] = this.sales_per_month
        if (this.median_sale_price_per_month != null) out["mspm"] = this.median_sale_price_per_month
        if (this.sales_per_year != null) out["spy"] = this.sales_per_year
        if (this.median_sale_price_per_year != null) out["mspy"] = this.median_sale_price_per_year
        if (this.average_annual_trend != null) out["aat"] = this.average_annual_trend
        if (this.recent_sales != null && this.recent_sales.length > 0) out["rs"] = this.recent_sales
        if (this.centroid != null) out["gc"] = this.centroid
        return out
    }
}

export default AreaAnalysis
